import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  REQUIRED_VEHICLE_IMAGE_KINDS,
  VEHICLE_IMAGE_GROUPS,
  type Vehicle,
  type VehicleImage,
  type VehicleImageGroup,
  type VehicleImageKind,
} from '@zuruflex/types';
import { Alert, Button, EmptyState } from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { ACCEPTED_IMAGE_TYPES, PhotoSlot } from './photo-slots.js';

/**
 * The gallery a car already has, and the gaps in it.
 *
 * Adding a car is one pass through a form; photographs arrive over days —
 * the outside from the forecourt on Monday, the cabin once it has been
 * cleaned. Without somewhere to come back to, a partner who saved a draft
 * with two exterior shots has no way to ever publish it.
 *
 * A section of the car's own page rather than the dialog it used to be:
 * photographs, paperwork and the rate are all things about one car, and
 * three overlapping popups were three ways to lose your place.
 */
export function PhotosPanel({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useTranslation(['partner', 'common']);
  const queryClient = useQueryClient();
  const extraInput = useRef<HTMLInputElement>(null);

  const [busyKind, setBusyKind] = useState<VehicleImageKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Read fresh rather than trusting the fleet list: an upload here changes
   * the listing status the moment a required shot lands, and the card
   * behind this dialog should not be the last to hear about it.
   */
  const { data: current } = useQuery({
    queryKey: ['vehicle', vehicle.id],
    queryFn: () => api.getVehicle(vehicle.id),
    initialData: vehicle,
  });

  const images = current.images;
  const byKind = new Map<VehicleImageKind, VehicleImage[]>();
  for (const image of images) {
    byKind.set(image.kind, [...(byKind.get(image.kind) ?? []), image]);
  }

  const missing = REQUIRED_VEHICLE_IMAGE_KINDS.filter((kind) => !byKind.has(kind));

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vehicle', vehicle.id] }),
      queryClient.invalidateQueries({ queryKey: ['my-fleet'] }),
    ]);

  const onFailure = (cause: unknown) =>
    setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'));

  const upload = useMutation({
    mutationFn: ({ kind, file }: { kind: VehicleImageKind; file: File }) =>
      api.uploadVehicleImage(vehicle.id, file, { kind }),
    onMutate: ({ kind }) => {
      setError(null);
      setBusyKind(kind);
    },
    onError: onFailure,
    onSettled: async () => {
      setBusyKind(null);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (imageId: string) => api.removeVehicleImage(vehicle.id, imageId),
    onMutate: () => setError(null),
    onError: onFailure,
    onSettled: () => refresh(),
  });

  const busy = upload.isPending || remove.isPending;

  return (
    <div>
      <p className="text-sm text-text-muted">{t('photos.subtitle')}</p>

      {error ? (
        <Alert variant="danger" className="mt-4">
          {error}
        </Alert>
      ) : null}

      <Alert variant={missing.length > 0 ? 'warning' : 'success'} className="mt-4">
        {missing.length > 0 ? (
          <>
            <span className="font-semibold">{t('photos.missing', { count: missing.length })}</span>{' '}
            {missing.map((kind) => t(`common:vehicleImageKind.${kind}`)).join(', ')}
          </>
        ) : (
          t('photos.complete')
        )}
      </Alert>

      {/* Removing a photo can pull a live listing down, so say so before
            they find out from the fleet page. */}
      {current.listingStatus === 'PUBLISHED' ? (
        <p className="mt-3 text-xs text-text-muted">{t('photos.live')}</p>
      ) : null}

      {(
        Object.entries(VEHICLE_IMAGE_GROUPS) as [VehicleImageGroup, readonly VehicleImageKind[]][]
      ).map(([group, kinds]) => (
        <section key={group} className="mt-6">
          <h3 className="text-sm font-semibold text-text">
            {t(`common:vehicleImageGroup.${group}`)}
          </h3>
          <div className="mt-3 grid gap-x-4 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
            {kinds.map((kind) => {
              const shots = byKind.get(kind) ?? [];
              const [first, ...rest] = shots;

              return (
                <PhotoSlot
                  key={kind}
                  kind={kind}
                  previewUrl={first?.url}
                  busy={busyKind === kind}
                  disabled={busy}
                  onPick={(file) => upload.mutate({ kind, file })}
                  onRemove={first ? () => remove.mutate(first.id) : undefined}
                  footer={
                    // Several scratches, several features: the kinds
                    // that come in sets show the rest underneath rather
                    // than pretending only one fits.
                    rest.length > 0 ? (
                      <ul className="mt-1 flex flex-wrap gap-1.5">
                        {rest.map((image) => (
                          <li key={image.id} className="relative">
                            <img
                              src={image.url}
                              alt={image.alt ?? ''}
                              className="size-12 rounded-field border border-border-default object-cover"
                            />
                            <button
                              type="button"
                              disabled={busy}
                              aria-label={`${t('photos.remove')} — ${t(`common:vehicleImageKind.${kind}`)}`}
                              onClick={() => remove.mutate(image.id)}
                              className="absolute -right-1 -top-1 rounded-full bg-ink-950/70 p-0.5 text-ink-50 hover:bg-ink-950"
                            >
                              <X className="size-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null
                  }
                />
              );
            })}
          </div>
        </section>
      ))}

      {images.length === 0 ? (
        <EmptyState title={t('photos.empty')} description={t('addCar.photosHint')} />
      ) : null}

      <div className="mt-6 border-t border-border-default pt-4">
        <input
          ref={extraInput}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          multiple
          hidden
          onChange={(event) => {
            for (const file of Array.from(event.target.files ?? [])) {
              upload.mutate({ kind: 'OTHER', file });
            }
            event.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => extraInput.current?.click()}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
          {t('photos.addOther')}
        </Button>
      </div>
    </div>
  );
}
